import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiActivity,
  FiAlertCircle,
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiExternalLink,
  FiMessageSquare,
  FiPower,
  FiRefreshCw,
  FiTool,
} from "react-icons/fi";
import { Sidebar } from "../components/Note";
import EmbeddedSignupButton from "../components/EmbeddedSignupButton";
import api from "../api";
import "../styles/tailwind.output.css";

const TILE_STATUS = {
  connected: {
    label: "conectado",
    pillClass: "border-emerald-500/45 bg-emerald-500/15 text-emerald-300",
  },
  available: {
    label: "disponible",
    pillClass: "border-sky-500/45 bg-sky-500/15 text-sky-300",
  },
  soon: {
    label: "proximamente",
    pillClass: "border-slate-600 bg-slate-800 text-slate-400",
  },
  disabled: {
    label: "requiere config",
    pillClass: "border-slate-700 bg-slate-900 text-slate-500",
  },
};

function IntegrationTile({ icon: Icon, title, description, status = "available", metaText = "", primaryAction = null, secondaryAction = null, isDark = true, className = "" }) {
  const statusInfo = TILE_STATUS[status] || TILE_STATUS.available;

  return (
    <article
      className={`rounded-2xl border p-5 shadow-[0_16px_42px_rgba(2,6,23,0.12)] ${
        isDark ? "border-[#25344f] bg-[#111a2b]" : "border-[#d4deec] bg-white"
      } ${className}`}
    >
      <div className="mb-4 flex items-start justify-between gap-2">
        <span
          className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${
            isDark ? "bg-[#182741] text-[#dce8fa]" : "bg-[#e7eef9] text-[#254062]"
          }`}
        >
          <Icon size={20} />
        </span>
        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusInfo.pillClass}`}>
          {statusInfo.label}
        </span>
      </div>

      <h3 className={`text-[35px] font-semibold leading-tight ${isDark ? "text-[#e4edf9]" : "text-[#14263f]"}`}>{title}</h3>
      <p className={`mt-2 min-h-[44px] text-sm leading-relaxed ${isDark ? "text-[#8ea5c4]" : "text-[#4e6889]"}`}>{description}</p>
      <p className={`mt-3 min-h-[18px] text-xs ${isDark ? "text-[#6e86a8]" : "text-[#6e86a8]"}`}>{metaText || " "}</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {primaryAction && (
          <button
            type="button"
            onClick={primaryAction.onClick}
            disabled={!!primaryAction.disabled}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
              primaryAction.disabled
                ? "cursor-not-allowed border-slate-700 bg-slate-900 text-slate-500"
                : "border-sky-500/45 bg-sky-500/15 text-sky-200 hover:border-sky-400 hover:bg-sky-500/20"
            }`}
          >
            {primaryAction.icon ? <primaryAction.icon size={14} /> : null}
            {primaryAction.label}
          </button>
        )}

        {secondaryAction && (
          <button
            type="button"
            onClick={secondaryAction.onClick}
            disabled={!!secondaryAction.disabled}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
              secondaryAction.disabled
                ? "cursor-not-allowed border-slate-700 bg-slate-900 text-slate-500"
                : "border-slate-700 bg-[#141f33] text-slate-300 hover:border-slate-500 hover:text-slate-200"
            }`}
          >
            {secondaryAction.icon ? <secondaryAction.icon size={14} /> : null}
            {secondaryAction.label}
          </button>
        )}
      </div>
    </article>
  );
}

function IntegracionesSelector() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => {
    if (typeof document === "undefined") return true;
    return document.documentElement.classList.contains("dark");
  });

  const [connectionStatus, setConnectionStatus] = useState(null);
  const [googleStatus, setGoogleStatus] = useState({
    user: { connected: false, calendar: { id: null, summary: null } },
  });

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingGoogle, setLoadingGoogle] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [showSignupPanel, setShowSignupPanel] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [actionBusy, setActionBusy] = useState("");

  const details = connectionStatus?.details || {};
  const isActuallyConnected = !!(details.phone_number_id && details.display_phone_number);
  const googleConnected = !!googleStatus?.user?.connected;
  const shouldShowSignupPanel = !loadingMeta && !isActuallyConnected && showSignupPanel;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const sync = () => setDark(root.classList.contains("dark"));
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    const onStorage = (event) => {
      if (event.key === "theme") sync();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const qualityInfo = useMemo(() => {
    const qualityRaw = String(details.quality_rating || "").trim().toUpperCase();
    if (!qualityRaw) {
      return {
        text: "Calificacion pendiente",
        className: "border-amber-500/35 bg-amber-500/12 text-amber-300",
      };
    }

    const map = {
      GREEN: { text: "Calidad alta", className: "border-emerald-500/35 bg-emerald-500/12 text-emerald-300" },
      HIGH: { text: "Calidad alta", className: "border-emerald-500/35 bg-emerald-500/12 text-emerald-300" },
      GOOD: { text: "Calidad alta", className: "border-emerald-500/35 bg-emerald-500/12 text-emerald-300" },
      YELLOW: { text: "Calidad media", className: "border-amber-500/35 bg-amber-500/12 text-amber-300" },
      MEDIUM: { text: "Calidad media", className: "border-amber-500/35 bg-amber-500/12 text-amber-300" },
      RED: { text: "Calidad baja", className: "border-rose-500/35 bg-rose-500/12 text-rose-300" },
      LOW: { text: "Calidad baja", className: "border-rose-500/35 bg-rose-500/12 text-rose-300" },
    };

    return map[qualityRaw] || {
      text: `Calidad ${qualityRaw.toLowerCase()}`,
      className: "border-slate-600 bg-slate-800 text-slate-300",
    };
  }, [details.quality_rating]);

  const loadMetaStatus = async ({ silent = false } = {}) => {
    if (!silent) setLoadingMeta(true);
    try {
      const response = await api.get("integration/connection-status/");
      const statusData = response?.data || null;
      const statusDetails = statusData?.details || {};

      const fullyConnected = !!(
        statusData?.is_connected
        && statusDetails?.phone_number_id
        && statusDetails?.display_phone_number
      );

      if (fullyConnected) {
        let nextStatus = statusData;
        try {
          const activeResp = await api.get("integration/meta-active-number/");
          const activeDetails = activeResp?.data?.details || activeResp?.data || {};
          const resolvedWaba = activeDetails?.waba_id || activeDetails?.active_waba_id;
          if (resolvedWaba) {
            nextStatus = {
              ...nextStatus,
              details: {
                ...nextStatus.details,
                waba_id: resolvedWaba,
              },
            };
          }
        } catch {
          // Fallback silencioso con el estado base.
        }

        setConnectionStatus(nextStatus);
        try {
          localStorage.removeItem("forceMetaReconnect");
        } catch {
          // noop
        }
      } else {
        try {
          const cfgResp = await api.get("integration/meta-config/");
          const cfg = cfgResp?.data || {};
          const hasCfgConnection = !!(cfg?.phone_number_id && cfg?.display_phone_number);
          if (hasCfgConnection) {
            setConnectionStatus({
              ...statusData,
              is_connected: true,
              details: {
                ...(statusData?.details || {}),
                phone_number_id: cfg.phone_number_id,
                display_phone_number: cfg.display_phone_number,
                waba_id: cfg.waba_id || statusData?.details?.waba_id || "",
              },
            });
          } else {
            setConnectionStatus(statusData);
          }
        } catch {
          setConnectionStatus(statusData);
        }
      }
    } catch {
      setConnectionStatus(null);
    } finally {
      setLoadingMeta(false);
    }
  };

  const loadGoogleStatus = async ({ silent = false } = {}) => {
    if (!silent) setLoadingGoogle(true);
    try {
      const { data } = await api.get("google/status/");
      setGoogleStatus(
        data || {
          user: { connected: false, calendar: { id: null, summary: null } },
        },
      );
    } catch {
      setGoogleStatus({ user: { connected: false, calendar: { id: null, summary: null } } });
    } finally {
      setLoadingGoogle(false);
    }
  };

  const refreshAll = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    await Promise.all([loadMetaStatus({ silent }), loadGoogleStatus({ silent })]);
    if (!silent) setRefreshing(false);
  };

  useEffect(() => {
    const forceReconnectFlag =
      typeof window !== "undefined"
      && localStorage.getItem("forceMetaReconnect") === "1";
    setShowSignupPanel(!!forceReconnectFlag);
    refreshAll({ silent: false });
  }, []);

  const handleEmbeddedSuccess = async () => {
    await refreshAll({ silent: true });
    window.setTimeout(() => {
      refreshAll({ silent: true });
    }, 2500);
  };

  const handleDisconnect = async () => {
    if (!window.confirm("¿Deseas desconectar esta integración?")) return;
    setActionBusy("disconnect");
    try {
      await api.post("integration/disconnect/");
      try {
        localStorage.setItem("forceMetaReconnect", "1");
      } catch {
        // noop
      }
      setShowSignupPanel(true);
      setVerifyResult(null);
      await refreshAll({ silent: true });
    } catch (err) {
      alert(String(err?.response?.data?.error || "No se pudo desconectar"));
    } finally {
      setActionBusy("");
    }
  };

  const handleVerifyConnection = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const response = await api.post("integration/meta-verify/");
      if (response?.data?.success) {
        setVerifyResult({
          success: true,
          message: "Conexión Meta verificada correctamente.",
          details: response.data.details,
        });
      } else {
        setVerifyResult({
          success: false,
          message: "No se pudo verificar la conexión.",
          error: response?.data?.error || "Error desconocido",
        });
      }
      await refreshAll({ silent: true });
    } catch (err) {
      setVerifyResult({
        success: false,
        message: "No se pudo verificar la conexión.",
        error: String(err?.response?.data?.error || err?.message || "Error desconocido"),
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleManualActivation = async () => {
    setActionBusy("manual-activation");
    try {
      const payload = {};

      if (details.phone_number_id) payload.phone_number_id = String(details.phone_number_id);
      if (details.display_phone_number) payload.display_phone_number = String(details.display_phone_number);

      try {
        const hintWaba = localStorage.getItem("hint_waba_id");
        const hintBusiness = localStorage.getItem("hint_business_id");
        if (hintWaba) payload.hint_waba_id = hintWaba;
        if (hintBusiness) payload.hint_business_id = hintBusiness;
      } catch {
        // noop
      }

      let clientPhone = "";
      try {
        clientPhone = String(localStorage.getItem("client_phone_number") || "").replace(/[^0-9]/g, "");
      } catch {
        clientPhone = "";
      }

      if (!clientPhone) {
        const raw = window.prompt(
          "Ingresa tu número de WhatsApp con código de país (ej. +593984088155):",
          details.display_phone_number || "",
        );
        const normalized = String(raw || "").replace(/[^0-9]/g, "");
        if (normalized) {
          clientPhone = normalized;
          try {
            localStorage.setItem("client_phone_number", normalized);
          } catch {
            // noop
          }
        }
      }

      if (clientPhone) payload.client_phone_number = clientPhone;

      const resp = await api.post("integration/meta-complete-signup-manual/", payload);
      const activeNumber =
        resp?.data?.details?.display_phone_number
        || resp?.data?.details?.phone_number
        || details.display_phone_number
        || "";

      alert(`Activación manual ejecutada${activeNumber ? ` para ${activeNumber}` : ""}.`);
      await refreshAll({ silent: true });
    } catch (err) {
      alert(String(err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Error"));
    } finally {
      setActionBusy("");
    }
  };

  const handleGoogleConnect = async () => {
    setActionBusy("google-connect");
    try {
      const { data } = await api.get("google/auth-url/", { params: { target: "user" } });
      const authUrl = data?.auth_url;
      if (!authUrl) {
        alert("No se pudo obtener la URL de autorización de Google.");
        return;
      }
      window.location.href = authUrl;
    } catch (err) {
      alert(String(err?.response?.data?.detail || err?.message || "Error al conectar Google"));
    } finally {
      setActionBusy("");
    }
  };

  const handleGoogleDisconnect = async () => {
    if (!window.confirm("¿Deseas desconectar Google Calendar?")) return;
    setActionBusy("google-disconnect");
    try {
      await api.post("google/disconnect/", { target: "user" });
      await loadGoogleStatus({ silent: true });
    } catch (err) {
      alert(String(err?.response?.data?.detail || "Error al desconectar Google"));
    } finally {
      setActionBusy("");
    }
  };

  const pageLoading = loadingMeta || loadingGoogle;

  return (
    <div className={`ws-page ws-page-integraciones flex min-h-screen ${dark ? "bg-[#050d1c] text-slate-100" : "bg-[#eef3fb] text-[#12243d]"}`}>
      <Sidebar />

      <main className="flex-1 px-4 py-6 md:px-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header className={`ws-header-surface flex flex-wrap items-center justify-between gap-4 rounded-2xl border px-6 py-5 ${dark ? "border-[#21324d] bg-[#10192a]" : "border-[#d4deec] bg-white"}`}>
            <div>
              <h1 className={`text-3xl font-bold tracking-tight ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Integraciones</h1>
              <p className={`mt-1 text-sm ${dark ? "text-[#8ba3c3]" : "text-[#4d688a]"}`}>Conexiones con servicios externos</p>
            </div>
          </header>

          <section className={`ws-panel rounded-2xl border p-5 shadow-[0_20px_50px_rgba(2,6,23,0.12)] md:p-6 ${dark ? "border-[#25344f] bg-[#111a2b]" : "border-[#d4deec] bg-white"}`}>
            <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#26d367] text-[#07131f]">
                  <FiMessageSquare size={23} />
                </span>
                <div>
                  <h2 className={`text-2xl font-semibold ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>WhatsApp Business API</h2>
                  <p className={`text-sm ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>Coexistencia Meta (On-Premise API)</p>
                </div>
              </div>

              <span
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${
                  isActuallyConnected
                    ? "border-emerald-500/45 bg-emerald-500/15 text-emerald-300"
                    : "border-amber-500/45 bg-amber-500/15 text-amber-300"
                }`}
              >
                <FiActivity size={14} />
                {isActuallyConnected ? "WhatsApp Conectado" : "WhatsApp sin conectar"}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className={`rounded-xl border p-4 ${dark ? "border-[#2a3b57] bg-[#151f31]" : "border-[#d4deec] bg-[#f8fbff]"}`}>
                <p className={`text-xs font-semibold uppercase tracking-wide ${dark ? "text-[#869dbc]" : "text-[#607b9d]"}`}>Numero conectado</p>
                <p className="mt-2 text-2xl font-bold text-[#40df8f]">
                  {isActuallyConnected ? (details.display_phone_number || "-") : "Sin conectar"}
                </p>
                <p className={`mt-2 text-sm ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>{isActuallyConnected ? "Numero verificado" : "Inicia Embedded Signup para conectar"}</p>
              </div>

              <div className={`rounded-xl border p-4 ${dark ? "border-[#2a3b57] bg-[#151f31]" : "border-[#d4deec] bg-[#f8fbff]"}`}>
                <p className={`text-xs font-semibold uppercase tracking-wide ${dark ? "text-[#869dbc]" : "text-[#607b9d]"}`}>WABA ID</p>
                <p className={`mt-2 break-all font-mono text-sm ${dark ? "text-[#d6e3f7]" : "text-[#1f3555]"}`}>{details.waba_id || "No disponible"}</p>
                <p className={`mt-2 text-sm ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>Cuenta de negocio conectada</p>
              </div>

              <div className={`rounded-xl border p-4 ${dark ? "border-[#2a3b57] bg-[#151f31]" : "border-[#d4deec] bg-[#f8fbff]"}`}>
                <p className={`text-xs font-semibold uppercase tracking-wide ${dark ? "text-[#869dbc]" : "text-[#607b9d]"}`}>Calidad de cuenta</p>
                <span className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${qualityInfo.className}`}>
                  {qualityInfo.text}
                </span>
                <p className={`mt-2 text-sm ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>
                  {details.verified_name ? `Nombre verificado: ${details.verified_name}` : "Completa la activación para mejorar la calificación"}
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[#25344f] pt-4">
              <button
                type="button"
                onClick={() => navigate("/plantillas-coexistencia")}
                className="inline-flex items-center gap-2 rounded-lg border border-[#6f86ff]/45 bg-gradient-to-r from-[#7f57ff]/45 to-[#2ed082]/45 px-4 py-2 text-sm font-semibold text-[#e8f2ff] transition hover:from-[#7f57ff]/55 hover:to-[#2ed082]/55"
              >
                <FiExternalLink size={14} />
                Ir a Plantillas de Coexistencia
              </button>

              <button
                type="button"
                onClick={isActuallyConnected ? handleDisconnect : () => setShowSignupPanel((v) => !v)}
                disabled={actionBusy === "disconnect"}
                className="inline-flex items-center gap-2 rounded-lg border border-rose-500/45 bg-rose-500/12 px-4 py-2 text-sm font-semibold text-rose-300 transition hover:border-rose-400 hover:bg-rose-500/18 disabled:cursor-not-allowed disabled:opacity-65"
              >
                <FiPower size={14} />
                {isActuallyConnected ? "Desconectar" : showSignupPanel ? "Ocultar conexión" : "Conectar con Meta"}
              </button>

              <button
                type="button"
                onClick={handleManualActivation}
                disabled={actionBusy === "manual-activation"}
                className="inline-flex items-center gap-2 rounded-lg border border-[#3a516f] bg-[#1a2f54] px-4 py-2 text-sm font-semibold text-[#cfe1ff] transition hover:border-[#4b6891] hover:text-white disabled:cursor-not-allowed disabled:opacity-65"
              >
                <FiTool size={14} />
                Completar activación
              </button>

              <button
                type="button"
                onClick={() => refreshAll({ silent: false })}
                disabled={refreshing}
                className="ml-auto inline-flex items-center gap-2 rounded-lg border border-[#3a516f] bg-[#17253d] px-4 py-2 text-sm font-semibold text-[#c5d7f0] transition hover:border-[#4b6891] hover:text-white disabled:cursor-not-allowed disabled:opacity-65"
              >
                <FiRefreshCw className={refreshing ? "animate-spin" : ""} size={14} />
                Actualizar estado
              </button>
            </div>

            {verifyResult && (
              <div
                className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
                  verifyResult.success
                    ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200"
                    : "border-rose-500/35 bg-rose-500/10 text-rose-200"
                }`}
              >
                <p className="font-semibold">{verifyResult.message}</p>
                {verifyResult.details?.phone_number && <p className="mt-1">Teléfono: {verifyResult.details.phone_number}</p>}
                {verifyResult.details?.quality_rating && <p>Calidad: {verifyResult.details.quality_rating}</p>}
                {verifyResult.error && <p className="mt-1 break-words">{verifyResult.error}</p>}
              </div>
            )}

            {shouldShowSignupPanel && (
              <div className={`mt-5 rounded-2xl border p-4 md:p-5 ${dark ? "border-sky-500/35 bg-sky-500/8" : "border-sky-300 bg-sky-50"}`}>
                <div className="mb-3 flex items-start gap-2">
                  <FiAlertCircle className={`mt-0.5 ${dark ? "text-sky-300" : "text-sky-700"}`} />
                  <p className={`text-sm ${dark ? "text-sky-100" : "text-sky-900"}`}>
                    Conecta o reconecta tu número oficial con el flujo Embedded Signup de Meta.
                    Si el número no aparece al primer intento, usa "Completar activación".
                  </p>
                </div>

                <EmbeddedSignupButton onSuccess={handleEmbeddedSuccess} />

                <button
                  type="button"
                  onClick={() => navigate("/integraciones/eventos")}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#3a516f] bg-[#17253d] px-4 py-2 text-sm font-semibold text-[#c5d7f0] transition hover:border-[#4b6891] hover:text-white"
                >
                  <FiExternalLink size={14} />
                  Ver eventos de Embedded Signup
                </button>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h2 className={`text-xl font-semibold ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Google Calendar</h2>

            <div className="mx-auto w-full max-w-[430px]">
              <IntegrationTile
                isDark={dark}
                icon={FiCalendar}
                title="Google Calendar"
                description="Sincroniza eventos y envia recordatorios automáticos."
                status={googleConnected ? "connected" : "available"}
                metaText={
                  googleConnected
                    ? `Calendario activo: ${googleStatus?.user?.calendar?.summary || googleStatus?.user?.calendar?.id || "principal"}`
                    : "Conecta tu cuenta para habilitar agenda y recordatorios"
                }
                primaryAction={{
                  label: googleConnected ? "Abrir módulo" : "Conectar Google",
                  onClick: googleConnected ? () => navigate("/soporte") : handleGoogleConnect,
                  icon: googleConnected ? FiArrowRight : FiExternalLink,
                  disabled: actionBusy === "google-connect",
                }}
                secondaryAction={{
                  label: googleConnected ? "Desconectar" : "Ir a soporte",
                  onClick: googleConnected ? handleGoogleDisconnect : () => navigate("/soporte"),
                  icon: googleConnected ? FiPower : FiArrowRight,
                  disabled: actionBusy === "google-disconnect",
                }}
                className="aspect-square"
              />
            </div>
          </section>

          {pageLoading && (
            <div className={`rounded-xl border px-4 py-3 text-sm ${dark ? "border-[#25344f] bg-[#10192a] text-[#8ea5c4]" : "border-[#d4deec] bg-white text-[#4d688a]"}`}>
              Cargando estado de integraciones...
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default IntegracionesSelector;